<template>
  <div
    id="feature-pdf-export"
    :class="['ui dimmer modals page', { active }]"
  >
    <div :class="['ui modal', { active }]">
      <i
        class="close icon"
        @click="closeModal"
      />
      <h2 class="ui icon header">
        <div class="content">
          Export PDF
          <div class="sub header">
            Choisissez les informations à afficher dans votre PDF
          </div>
        </div>
      </h2>
      <div class="content">
        <form
          id="form-feature-export"
          class="ui form"
        >
          <div class="ui segments">
            <div class="ui segment">
              <div :class="['ui toggle checkbox', { 'disabled': mapDisabled || exportingFeature }]">
                <input
                  id="include-map"
                  v-model="includeMap"
                  type="checkbox"
                  name="include-map"
                >
                <label for="include-map">Inclure la carte
                  <i class="map outline icon" />
                </label>
              </div>
            </div>
            <div class="ui segment">
              <div :class="['ui toggle checkbox', { 'disabled': exportingFeature }]">
                <input
                  id="include-photos"
                  v-model="includePhotos"
                  type="checkbox"
                  name="include-photos"
                >
                <label for="include-photos">Inclure les photos des pièces jointes
                  <i class="images outline icon" />
                </label>
              </div>
            </div>
          </div>
          <!-- Select batch size -->
          <div
            v-if="!singleFeature"
            :class="['ui inline field', { 'disabled': exportingFeature }]"
          >
            <label for="include-photos">Nombre de signalement par pdf</label>
            <div class="ui compact menu">
              <div class="ui simple dropdown item">
                {{ pageSize }}
                <i class="dropdown icon" />
                <div class="menu">
                  <div
                    v-for="sizeOption in [1, 10, 25, 50, 100, 250, 500, 1000]"
                    :key="sizeOption"
                    class="item"
                    @click="pageSize = sizeOption"
                  >
                    {{ sizeOption }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Custom Fields -->
          <div class="ui horizontal divider">
            DONNÉES À INCLURE
          </div>
          <!-- Loop over each feature types -->
          <div class="grouped fields">
            <div
              v-for="featType in featureTypesForm"
              :key="featType.slug"
            >
              <div class="field">
                <label>{{ featType.title }}</label>
                <Multiselect
                  v-model="featType.selectedCustomFields"
                  :options="[...standardFields, ...featType.customfield_set]"
                  :multiple="true"
                  :searchable="true"
                  :close-on-select="false"
                  :show-labels="false"
                  :max="maxAttributes"
                  :disabled="exportingFeature"
                  track-by="name"
                  label="label"
                  :placeholder="featType.selectedCustomFields.length === 0 ?
                    'Sélectionner les attributs à inclure' : ''"
                >
                  <template #maxElements>
                    {{ maxAttributes }} options maximum autorisées.
                    Veuillez d'abord retirer une option sélectionnée pour en choisir une autre.
                  </template>
                </Multiselect>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="actions">
        <div class="progress-bar-container">
          <div
            v-if="exportingFeature || exportDone"
            class="ui progress"
          >
            <div :class="['ui teal progress', { active: exportingFeature, success: exportDone }]">
              <div
                class="bar"
                :style="{ width: progress + '%' }"
              />
              <div class="label">
                Export 
                <span v-if="exportDone">terminé</span>
                <span v-else>de</span> 
                {{ Math.ceil(pdfProgressSignalements / 100) }}
                sur {{ featuresCount }} signalement{{ featuresCount > 1 ? 's' : '' }}

                <template v-if="totalBatches > 1">
                  – partie {{ pdfProgressBatch >= totalBatches ? totalBatches : pdfProgressBatch + 1 }} 
                  / {{ totalBatches }}
                </template>
              </div>
            </div>
          </div>
        </div>

        <button
          id="save-changes"
          type="button"
          :class="[
            'ui teal icon button',
            { loading: exportingFeature },
            { disabled: !isOnline },
          ]"
          @click="exportAllPdfs"
        >
          <i
            class="white file pdf icon"
            aria-hidden="true"
          />
          Exporter les signalements
        </button>
      </div>

      <!-- Hidden map to capture map image -->
      <div class="hidden-map-container">
        <div
          id="export-map"
          ref="mapExport"
        />
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { buffer } from 'ol/extent';
import Map from 'ol/Map.js';
import OSM from 'ol/source/OSM.js';
import TileLayer from 'ol/layer/Tile.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import View from 'ol/View.js';
import { GeoJSON } from 'ol/format';
import { Fill, Stroke, Style, Circle } from 'ol/style';
import { transform } from 'ol/proj';
import { Attribution } from 'ol/control';

import { mapActions, mapGetters, mapState } from 'vuex';
import Multiselect from 'vue-multiselect';

import featureAPI from '@/services/feature-api';
import axios from '@/axios-client.js';
import { formatStringDate, buildPdfMatrix, loadImage, hexToRgb, generatePdfFilename  } from '@/utils';

const defaultFields = [
  { label: 'Description', name: 'description', field_type: 'texte' },
  { label: 'Statut', name: 'status', field_type: 'char' },
  { label: 'Auteur', name: 'display_creator', field_type: 'char' },
  { label: 'Mis à jour le', name: 'updated_on', field_type: 'date' },
];

export default {
  name: 'FeaturesPdfExportModal',

  components: {
    Multiselect,
  },

  props: {
    selectedId: {
      type: String,
      default: null
    },
    active: {
      type: Boolean,
      default: false
    },
    queryparams: {
      type: Object,
      default: () => {}
    },
    singleFeature: {
      type: Object,
      default: () => {}
    },
  },

  data() {
    return {
      cancelPdfExport: false,
      drawSource: null,
      exportingFeature: false,
      fetchedCount: 0,     
      includeMap: true,
      includePhotos: true,
      map: null,
      mapDisabled: false,
      maxAttributes: 5,
      nextPage: null,
      offset: 0,
      pageSize: 50,
      pdfProgressSignalements: 0,
      pdfProgressBatch: 0,
      previousPage: null,
      projectSlug: this.$route.params.slug,
      featureTypesForm: [],
      totalBatches: 0,
      vectorLayer: null,
    };
  },

  computed: {
    ...mapState([
      'configuration',
      'isOnline',
    ]),
    ...mapState('projects', [
      'project',
    ]),
    ...mapState('feature-type', [
      'feature_types',
    ]),
    ...mapGetters('feature', [
      'massSelectionPayload',
      'selectedTypeCounts',
    ]),

    API_BASE_URL() {
      return this.configuration.VUE_APP_DJANGO_API_BASE;
    },

    DJANGO_BASE_URL() {
      return this.configuration.VUE_APP_DJANGO_BASE;
    },

    standardFields() {
      return [
        ...defaultFields,
        // Add field choice if enabled in project
        ...(this.project.feature_assignement ? [{ label: 'Membre assigné', name: 'assigned_member', field_type: 'char' }] : [])
      ];
    },

    featuresCount() {
      return this.singleFeature ? 1 : this.fetchedCount;
    },

    progress() {
      if (!this.featuresCount || this.featuresCount === 0) return 0;
      const progress = this.pdfProgressSignalements / this.featuresCount;
      return progress <= 100 ? progress : 100; // Prevent overflow if errors
    },

    exportDone () {
      return this.progress > 99;
    }
  },

  watch: {
    active(newValue) {
      if (newValue === true) { // Opening the modal
        this.initForms();
        // Reinit values if modal was closed previously
        this.resetExportState();
        this.cancelPdfExport = false;
        this.exportingFeature = false;
      }
    }
  },

  mounted() {
    this.initPage();
  },

  methods : {
    ...mapActions('projects', [
      'GET_PROJECT_INFO',
      'GET_PROJECT',
    ]),

    async initPage() {
      await this.getPageInfo();
      this.$nextTick(() => {
        this.initMap();
      });
    },

    async getPageInfo() {
      let promises = [];
      if (!this.project) {
        promises.push(this.GET_PROJECT(this.projectSlug));
      }
      if (!this.feature_types) {
        promises.push(
          this.GET_PROJECT_INFO(this.projectSlug),
        );
      }
      await axios.all(promises);
    },

    initForms() {
      this.includeMap = true;
      this.includePhotos = true;
      this.featureTypesForm = [];
      console.log('[debug] singleFeature', this.singleFeature);
      console.log('[debug] feature_types', this.feature_types);
      // Get the feature types present in the feature selection
      for (const { slug, title, geom_type, customfield_set } of this.feature_types || []) {
        const inSelection = this.singleFeature ?
          slug === this.singleFeature?.properties?.feature_type?.slug :
          slug in this.selectedTypeCounts;
        if (inSelection) {
          this.featureTypesForm.push({
            slug,
            title,
            geom_type,
            customfield_set,
            selectedCustomFields: []
          });
        }
      }
      console.log('[debug] featureTypesForm', this.featureTypesForm);
      this.checkHasMap();
    },

    resetExportState() {
      this.nextPage = null;
      this.previousPage = null;
      this.pdfProgressSignalements = 0;
      this.pdfProgressBatch = 0;
      this.totalBatches = 0;
    },

    checkHasMap() {
      // Check if there is at least one geographic feature type
      const hasGeom = this.featureTypesForm.some(el => el.geom_type !== 'none');
      this.mapDisabled = !hasGeom;
      this.includeMap = hasGeom;
    },

    closeModal() {
      if (this.exportingFeature) {
        console.log('Cancelling export and closing modal');
        this.cancelPdfExport = true;
        this.exportingFeature = false;
      }
      this.$emit('close');
    },

    initMap() {
      const raster = new TileLayer({
        source: new OSM(),
      });

      this.drawSource = new VectorSource();

      this.vectorLayer = new VectorLayer({
        source: this.drawSource,
        zIndex: 29,
      });
      this.map = new Map({
        target: this.$refs.mapExport,
        layers: [raster, this.vectorLayer],
        controls: [new Attribution({ collapsible: false })],
        view: new View({
          center: transform([1.43, 43.6], 'EPSG:4326', 'EPSG:3857'),
          zoom: 15,
        }),
      });
    },

    async addFeatureToMap(feature) {
      console.log('Adding feature to map: ', feature);
      if (!this.map || !this.drawSource) {
        console.warn('Map not initialized');
        return;
      }

      // Remove previous feature
      this.drawSource.clear();

      const format = new GeoJSON();

      try {
        const olFeature = format.readFeature(feature, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        });

        this.drawSource.addFeature(olFeature);
      } catch (err) {
        console.error(err);
      }

      // Define the style for the feature.
      const defaultStyle = new Style({
        image: new Circle({
          fill: new Fill({ color: [0, 0, 0, 0.5] }),
          stroke: new Stroke({ color: [0, 0, 0], width: 4 }),
          radius: 10,
        }),
        stroke: new Stroke({ color: [0, 0, 0], width: 4 }),
        fill: new Fill({ color: [0, 0, 0, 0.5] }),
      });

      this.vectorLayer.setStyle(defaultStyle);

      // Center map on feature extent + wait for render
      console.log('Centering map on feature');
      await this.fitExtent(
        buffer(this.drawSource.getExtent(), 200)
      );
    },

    fitExtent(ext) {
      return new Promise(resolve => {
        const map = this.map;
        const view = map.getView();
        // Listen to the final render to resolve the promise
        map.once('rendercomplete', () => {
          console.log('rendercomplete');
          resolve();
        });
        // Trigger the change that will re-render the map
        view.fit(ext, { padding: [25, 25, 25, 25] });
      });
    },

    formatValue(value, field_type) {
      if (field_type === 'date') {
        return formatStringDate(value) || '-';
      } else if (field_type === 'boolean') {
        return value ? 'Oui' : 'Non';
      }
      return value ? value.toString() : '-';
    },

    getAttributes(feature) {
      let infos = [];
      const form = this.featureTypesForm.find(el => el.slug === feature.properties.feature_type.slug);

      for (const { label, name, field_type } of form.selectedCustomFields) {
        const rawValue = name === 'status' ? feature.properties[name].label : feature.properties[name];
        const value = this.formatValue(rawValue, field_type) || '';
        infos.push({ label, value });
      }
      return infos;
    },

    async fetchFeatureSelection(url=this.nextPage) {
      if (!url && this.queryparams) {
        const params = new URLSearchParams();
        const paramObject = Object.entries({
          ...this.queryparams,
          output: 'geojson',
          offset: this.offset,
          limit: this.pageSize,
          exclude: this.massSelectionPayload.exclude,
        });
          // Sanitize params to exclude undefined or null values
        for (const [key, value] of paramObject) {
          if (value !== undefined && value !== null) {
            params.append(key, value);
          }
        }
        // Proccess array in order to comply to params format
        this.massSelectionPayload.feature_ids.forEach(id => {
          params.append('feature_ids', id);
        });

        // Build the paginated request URL
        const queryString = params.toString();
        url = `${this.API_BASE_URL}projects/${this.projectSlug}/feature-paginated/?${queryString}`;
      }

      return featureAPI.getPaginatedFeatures(url)
        .then((data) => {
          if (data?.cancelled) return;
          if (data) {
            this.nextPage = data.next ? new URL(data.next, this.DJANGO_BASE_URL).toString() : null;
            this.previousPage = data.previous ? new URL(data.previous, this.DJANGO_BASE_URL).toString() : null;
            this.fetchedCount = data.count;
            return data.results;
          }
        })
        .catch((error) => console.error('Erreur features :', error));
    },

    async exportAllPdfs() {
      this.resetExportState();
      this.exportingFeature = true;
      this.cancelPdfExport = false;

      // Load logo once, since it doesn't change
      let logoImg = null;
      if (this.configuration.VUE_APP_LOGO_PATH) {
        console.log('[PDF export] Capturing Logo');
        try { 
          logoImg = await loadImage(this.configuration.VUE_APP_LOGO_PATH, 512); 
        } catch (e) { 
          console.warn(e); 
        }
      }
      // Determine feature type title for the whole selection
      let featureTypeTitle;
      const typeKeys = Object.keys(this.selectedTypeCounts);
      if (typeKeys.length > 1) {
        featureTypeTitle = 'Multiple Types';
      } else {
        featureTypeTitle = this.feature_types.find(
          ft => ft.slug === typeKeys[0]
        )?.title || '';
      }

      const processBatch = async () => {
        // 🔴 Global cancellation
        if (this.cancelPdfExport) {
          console.warn('[PDF export] Export cancelled by user');
          this.exportingFeature = false;
          return;
        }

        let features = [];

        if (this.singleFeature) {
          features = [this.singleFeature];
        } else {
          const data = await this.fetchFeatureSelection();
          features = data?.features || [];
          console.log('[PDF export] Features fetched:', features);
        }

        if (!features.length) {
          this.exportingFeature = false;
          console.error('[PDF export] No features found to export');
          return;
        }

        // Initialize totalBatches to the first page
        if (!this.totalBatches) {
          this.totalBatches = this.singleFeature
            ? 1
            : Math.ceil((this.featuresCount) / this.pageSize);
        }

        let pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        for (let i = 0; i < features.length; i++) {

          // 🔴 Fine cancellation (during a batch)
          if (this.cancelPdfExport) {
            console.warn('[PDF export] Export cancelled during batch');
            this.exportingFeature = false;
            return;
          }
          const feature = features[i];

          // Update feature progress (use integer increments to avoid floating-point precision errors)
          this.pdfProgressSignalements += 20;
          console.log('[PDF export] Treating feature #', i + 1, feature);

          // 🗺️ Capture map
          let mapCanvas;
          if (this.includeMap && feature.geometry) {
            console.log('[PDF export] Capturing Map');
            await this.addFeatureToMap(feature);
            console.log('[PDF export] Feature added to Map');
            if (!this.$refs.mapExport) {
              console.warn('[PDF export] Map element missing, skipping canvas capture for feature', i + 1);
            } else {
              await new Promise(requestAnimationFrame); // Force a delay to let the render complete
              mapCanvas = await html2canvas(this.$refs.mapExport, { useCORS: true, allowTaint: true, scale: 1 });
              console.log('[PDF export] Canvas retrieved');
            }
          }

          // Update feature progress
          this.pdfProgressSignalements += 20;

          
          // 📷 Load photos
          const photos = [];
          if (this.includePhotos && feature.properties.attachments_images?.length) {
            console.log('[PDF export] Capturing Photos', feature);
            const photoUrls = feature.properties.attachments_images.slice(0, 3);
            const photoStep = 30 / photoUrls.length; // répartir 20% sur les photos
            for (const url of photoUrls) {
              if (url) {
                const canvasPhoto = await loadImage(this.DJANGO_BASE_URL + url, 1024);
                photos.push(canvasPhoto);
              }
              // Update feature progress
              this.pdfProgressSignalements += photoStep;
            }
          } else {
            // If no photos, we consider this part as done
            this.pdfProgressSignalements += 30; 
          }

          // Find feature type of the current feature
          const featureType = this.feature_types.find(el => el.slug === feature.properties.feature_type.slug);

          // 🗃️ Prepare data
          let dataPdf = {
            ftTitle: featureType?.title?.toUpperCase() || 'TYPE DE SIGNALEMENT',
            title: feature.properties.title || 'Titre du signalement',
            createdDate: formatStringDate(feature.properties.created_on),
            attributes: this.getAttributes(feature),
            themeColor: hexToRgb(this.configuration.VUE_APP_PRIMARY_COLOR),
            logoImg,
            mapCanvas,
            photos,
            index: i,
            total: features.length
          };

          // Update feature progress
          this.pdfProgressSignalements += 20;
          console.log('[PDF export] Data ready to export');

          // Build the pdf page
          if (i > 0) pdf.addPage();
          buildPdfMatrix(pdf, dataPdf);

          // 🧹 Clean heavy objects to free memory
          if (mapCanvas) {
            mapCanvas.width = 0;
            mapCanvas.height = 0;
            mapCanvas = null;
          }
          photos.length = 0;
          dataPdf = null;
          // Let the rendering cycle complete and allow the browser to process pending tasks.
          // Does not force garbage collection, but helps avoid long blocking loops.
          await new Promise(requestAnimationFrame);

          // Update feature progress
          this.pdfProgressSignalements += 10;
        }

        // 💾 Save PDF with explicit filename

        // Update batch progress
        this.pdfProgressBatch++;

        // Determine number of features in this file
        const featuresCountInFile = features.length;

        // Name the PDF
        const pdfName = generatePdfFilename({
          featuresCountInFile,
          featureTitle: featuresCountInFile === 1 ? features[0].properties.title : null,
          projectTitle: this.project?.title || this.project?.slug,
          featureTypeTitle,
          batchIndex: this.totalBatches > 1 ? this.pdfProgressBatch : null,
          totalBatches: this.totalBatches > 1 ? this.totalBatches : null
        });

        // Trigger download
        pdf.save(pdfName);
        // 🧹 Free PDF memory
        pdf = null;
        // Let the rendering cycle complete and allow the browser to process pending tasks.
        // Does not force garbage collection, but helps avoid long blocking loops.
        await new Promise(requestAnimationFrame);

        // 🔁 Chaining only if pagination
        if (!this.singleFeature && this.nextPage && !this.cancelPdfExport) {
          await processBatch();
        } else {
          this.exportingFeature = false;
        }
      };

      // 🚀 Launch
      await processBatch();
    },
  }
};
</script>

<style lang="less">
#feature-pdf-export {
  .hidden-map-container {
    position: absolute;
    top: -10000px;
    left: -10000px;
    width: 512px;   /* fix size for the capture */
    height: 512px;
    pointer-events: none;
    opacity: 0;
  }
  #export-map {
    width: 100%;
    height: 100%;
    border: 1px solid grey;
    .ol-attribution {
      ul, a {
        color: #666666;
        font-size: 18px !important;
      }
    }
  }

  .ui.dimmer.active {
    display: flex !important
  }

  .ui.checkbox, .ui.checkbox > label {
    width: 100%;
  }
  .ui.checkbox > label {
    display: flex;
    justify-content: space-between;
  }

  .ui.modal > .actions {
    display: flex;
    justify-content: space-between;
    height: 5.5em;
    .ui.button {
      white-space: nowrap;
      width: 14em;
    }
    .progress-bar-container {
      width: calc(100% - 15em);
      padding-right: 1em;
      .ui.progress, .ui.progress .bar {
        border-radius: 7px;
      }
      .ui.progress > .label {
        white-space: nowrap;
      }
    }
  }
}
</style>
